import { InputGroup, Form, Dropdown } from "react-bootstrap";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { Popover } from "react-tiny-popover";
import { toast } from "react-toastify";
import currency from "currency.js";
import DateTime from "react-datetime";

import { useAuth } from "../../hooks/useAuth";
import "../../assets/scss/scoped/issueItem.scss";
import GetTemplateModal from "./GetTemplateModal";
import MagnifyIcon from "mdi-react/MagnifyIcon";
import DotsVerticalIcon from "mdi-react/DotsVerticalIcon";
import { DeleteIcon } from "../Icons";
import { format } from "date-fns";
import moment from "moment";

// Custom hook for localStorage with error handling
// const useLocalStorage = (key, defaultValue = null) => {
//   try {
//     const item = window.localStorage.getItem(key);
//     return item ? JSON.parse(item) : defaultValue;
//   } catch (error) {
//     console.error(`Error reading localStorage key "${key}":`, error);
//     return defaultValue;
//   }
// };

export const EditProductionPlanTableRow = ({
  planId,
  allPlans,
  plan,
  handleRemove,
  index,
  edit,
  initProps,
  isNewRow = false,
}) => {
  const [showItemsPopover, setShowItemsPopover] = useState(false);
  const { user } = useAuth();
  // const plantLocal = useLocalStorage("plantDetails", {});
  const isInitializedRef = useRef(false);
  const previousPlanRef = useRef(plan);
  const DateTimeRef = useRef(null);

  useEffect(() => {
    if (DateTimeRef.current) {
      if (DateTimeRef.current?.style) {
        DateTimeRef.current.style.zIndex = "100";
        DateTimeRef.current.style.position = "fixed"; // Ensure it is positioned correctly
      }
    }
  }, [DateTimeRef]);

  const initialValues = useMemo(
    () => ({
      Bar_Code: "",
      Item_Name: "",
      Quantity: 0,
      UnitCost: 0,
      Date_Log: format(new Date(), "yyyy-MM-dd"),
      prod_profiles: [],
      dueDate: new Date(),
    }),
    [],
  );

  const formik = useFormik({
    initialValues,
    validationSchema: yup.object().shape({}),
    onSubmit: (values) => {},
  });

  const setValues = formik.setValues;

  // Memoized setup function to prevent unnecessary re-renders
  const setUp = useCallback(() => {
    if (!isNewRow && plan && plan === previousPlanRef.current) {
      const unitCost = plan?.prod_profiles?.reduce(
        (acc, cost) => acc + Number(cost.UnitCost) * cost.quantity,
        0,
      );
      // console.log(plan);

      try {
        const safeValues = {
          Bar_Code: plan.Bar_Code || "",
          Item_Name: plan.Item_Name || "",
          Quantity: Number(plan.Quantity) || 1,
          UnitCost: unitCost || 0,
          Date_Log: format(new Date(), "yyyy-MM-dd"),
          prod_profiles: Array.isArray(plan.prod_profiles)
            ? plan.prod_profiles
            : [],
          dueDate: plan.dueDate ? new Date(plan.dueDate) : new Date(),
        };
        setValues(safeValues);
        previousPlanRef.current = "nothing";
        isInitializedRef.current = true;
      } catch (error) {
        console.error("Error setting form values:", error);
        toast.error("Failed to load plan data");
      }
    }
  }, [plan, isNewRow, setValues]);

  // Initialize form values only once when plan changes
  useEffect(() => {
    setUp();
  }, [setUp]);

  // Handle form value changes with debouncing to prevent infinite loops
  const handleFormChange = useCallback(() => {
    if (!isNewRow && isInitializedRef.current) {
      const formValues = { ...formik.values };

      // Ensure all required fields exist
      for (let k in initialValues) {
        if (!formValues.hasOwnProperty(k)) {
          formValues[k] = initialValues[k];
        }
      }

      const updatedFormValues = {
        ...formValues,
        UserName: user.username,
        plant: planId,
        Plan_Id: planId,
      };

      edit({
        index,
        formValues: updatedFormValues,
      });
    }
  }, [
    formik.values,
    isNewRow,
    planId,
    edit,
    index,
    initialValues,
    user.username,
  ]);

  // Debounced effect for form changes
  useEffect(() => {
    if (!isNewRow && isInitializedRef.current) {
      const timeoutId = setTimeout(handleFormChange, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [handleFormChange, isNewRow]);

  // const handleSelectedItem = useCallback(
  //   (selectedItem) => {
  //     if (selectedItem?.RawMaterialBarcode) {
  //       const {
  //         rawItems,
  //         prod_profiles: finished = [],
  //         ...others
  //       } = selectedItem;
  //       const UnitCost = Number(others?.totalCost) / finished?.length;
  //       const otherRaws = [others, ...JSON.parse(rawItems)];
  //       const finishedItems = finished?.map((item) => ({
  //         Bar_Code: item?.FinishedItemBarcode,
  //         Cat_Name: "",
  //         Date_Log: moment(item?.Date).format("YYYY-MM-DD"),
  //         EntryTime: moment(item?.Date).format("HH:mm:ss"),
  //         Item_Desc: others?.templateName,
  //         Item_Name: item?.FinishedItemName,
  //         Product_Model: item?.FinishedItemSize,
  //         Product_Name: item?.processTo,
  //         UnitCost,
  //         accountName: item?.accountName,
  //       }));
  //       const prod_profiles = otherRaws?.map((item) => ({
  //         profileid: others?.Material_ID,
  //         Product_Model: item?.RawMaterialSize,
  //         UnitCost: item?.RawMaterialUnitCost ?? item?.UnitCost,
  //         item: item?.RawMaterialBarcode ?? item?.Bar_Code,
  //         quantity: item?.RawMaterialQuantity,
  //         regdate: moment(others?.Date).format("YYYY-MM-DD"),
  //         title: item?.RawMaterialItemName,
  //       }));

  //       // console.log(finishedItems, prod_profiles);
  //       selectedItem.isArray = true;
  //       selectedItem.prod_profiles = prod_profiles;
  //       selectedItem.finishedItems = finishedItems;
  //     }
  //     // console.log(selectedItem);

  //     const existingPlan = allPlans.findIndex(
  //       (plan) => plan.Bar_Code === selectedItem.Bar_Code,
  //     );
  //     if (existingPlan >= 0 && existingPlan !== index) {
  //       toast.error("Item already exists in the plan");
  //       return;
  //     }
  //     if (existingPlan >= 0 && existingPlan === index) {
  //       setShowItemsPopover(false);
  //       return; // If the item is already in the current row, just update the values
  //     }
  //     const newPlanData = {
  //       ...selectedItem,
  //       Quantity: 1,
  //       UserName: user.username,
  //       plant: planId,
  //       Date_Log: format(new Date(), "yyyy-MM-dd"),
  //       Plan_Id: planId,
  //       dueDate: new Date(),
  //     };

  //     setValues({
  //       Bar_Code: selectedItem.Bar_Code,
  //       Item_Name: selectedItem.Item_Name,
  //       Quantity: 1,
  //       UnitCost: selectedItem.UnitCost || 0,
  //       Date_Log: format(new Date(), "yyyy-MM-dd"),
  //       prod_profiles: selectedItem.prod_profiles || [],
  //       dueDate: selectedItem.dueDate
  //         ? new Date(selectedItem.dueDate)
  //         : new Date(),
  //     });
  //     // Trigger edit to add this as a new plan
  //     // console.log(newPlanData);

  //     edit({
  //       index,
  //       formValues: newPlanData,
  //       isNewRow: index >= allPlans.length,
  //     });

  //     setShowItemsPopover(false);
  //   },
  //   [edit, index, setValues, user.username, planId, allPlans],
  // );

  const handleSelectedItem = useCallback(
    (selectedItem) => {
      // Normalize to a list of "rows to add" — one item for the normal
      // case, one per finished item for the raw-material case. This lets
      // everything below (duplicate check, edit calls) be a single loop
      // instead of two separate code paths.
      let items = [selectedItem];
      let recipeProfiles = selectedItem?.prod_profiles || [];

      if (selectedItem?.RawMaterialBarcode) {
        const {
          rawItems,
          prod_profiles: finished = [],
          ...others
        } = selectedItem;

        const UnitCost = Number(others?.totalCost) / (finished?.length || 1);
        const otherRaws = [others, ...JSON.parse(rawItems || "[]")];

        // Recipe lines — same for every finished item produced from
        // this raw material batch, so computed once and shared.
        recipeProfiles = otherRaws?.map((item) => ({
          profileid: others?.Material_ID,
          Product_Model: item?.RawMaterialSize,
          UnitCost: item?.RawMaterialUnitCost ?? item?.UnitCost,
          item: item?.RawMaterialBarcode ?? item?.Bar_Code,
          quantity: item?.RawMaterialQuantity,
          regdate: moment(others?.Date).format("YYYY-MM-DD"),
          title: item?.RawMaterialItemName,
          templateName: others?.templateName,
        }));

        items = finished.map((item) => ({
          ...others,
          Bar_Code: item?.FinishedItemBarcode,
          Cat_Name: "",
          Date_Log: moment(item?.Date).format("YYYY-MM-DD"),
          EntryTime: moment(item?.Date).format("HH:mm:ss"),
          Item_Desc: others?.templateName,
          Item_Name: item?.FinishedItemName,
          Product_Model: item?.FinishedItemSize,
          Product_Name: item?.processTo,
          UnitCost,
          accountName: item?.accountName,
          // prod_profiles is deliberately NOT set here — it's the raw
          // material batch's recipe, shared across all finished items
          // produced from it, not a per-row value. Attaching it to every
          // row here duplicates it (finished.length x) wherever the plan
          // aggregates prod_profiles across rows. It's attached to just
          // the first row below instead.
          isConversion: true,
        }));
      }

      if (!items.length) return;

      // Reject the whole selection if any item collides with an existing
      // row that isn't the one currently being edited — same rule as
      // before, just applied per item.
      for (const item of items) {
        const existingPlan = allPlans.findIndex(
          (plan) => plan.Bar_Code === item.Bar_Code,
        );
        if (existingPlan >= 0 && existingPlan !== index) {
          toast.error(
            `"${item.Item_Name || item.Bar_Code}" already exists in the plan`,
          );
          return;
        }
      }

      // The first item reuses the row that triggered this popover
      // (`index`). Any additional items — only possible when a raw
      // material produced multiple finished items — get appended as new
      // rows right after the current plan list.
      let nextNewIndex = Math.max(allPlans.length, index + 1);

      items.forEach((item, i) => {
        const rowIndex = i === 0 ? index : nextNewIndex++;
        const isNewRow = rowIndex >= allPlans.length;

        // Item already sitting in this exact row — nothing to change.
        const alreadyHere = allPlans[rowIndex]?.Bar_Code === item.Bar_Code;
        if (alreadyHere && !isNewRow) return;

        const newPlanData = {
          ...item,
          Quantity: 1,
          UserName: user.username,
          plant: planId,
          Date_Log: format(new Date(), "yyyy-MM-dd"),
          Plan_Id: planId,
          dueDate: new Date(),
          // Only the first row carries the shared recipe — attaching it
          // to every row would duplicate it wherever prod_profiles gets
          // aggregated across the plan's rows.
          prod_profiles: i === 0 ? recipeProfiles : [],
        };

        // Only the row that triggered the popover drives the visible
        // form state — the rest are added directly via `edit`.
        if (i === 0) {
          setValues({
            Bar_Code: item.Bar_Code,
            Item_Name: item.Item_Name,
            Quantity: 1,
            UnitCost: item.UnitCost || 0,
            Date_Log: format(new Date(), "yyyy-MM-dd"),
            prod_profiles: recipeProfiles,
            dueDate: item.dueDate ? new Date(item.dueDate) : new Date(),
          });
        }

        edit({ index: rowIndex, formValues: newPlanData, isNewRow });
      });

      setShowItemsPopover(false);
    },
    [edit, index, setValues, user.username, planId, allPlans],
  );

  return (
    <tr>
      <td>
        {!isNewRow && (
          <Dropdown>
            <Dropdown.Toggle
              variant=""
              className="bg-white border-0"
              bsPrefix="print more"
            >
              <DotsVerticalIcon />
            </Dropdown.Toggle>
            <Dropdown.Menu
              popperConfig={{
                strategy: "fixed",
              }}
              renderOnMount
              className="dropdown-with-icons"
            >
              <Dropdown.Item as="button" onClick={() => handleRemove(index)}>
                <DeleteIcon />
                Remove
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        )}
      </td>
      <td>{index + 1}</td>
      <td>
        <InputGroup className="flex-nowrap">
          <Form.Control
            name="Bar_Code"
            value={formik.values.Bar_Code}
            onChange={formik.handleChange}
            readOnly={!isNewRow}
            placeholder={isNewRow ? "Select item..." : ""}
          />
          {/* {isNewRow && ( */}
          <Popover
            isOpen={showItemsPopover}
            onClickOutside={() => setShowItemsPopover(false)}
            content={() => (
              <GetTemplateModal handleSelectedItem={handleSelectedItem} />
            )}
            position="bottom"
          >
            <InputGroup.Text onClick={() => setShowItemsPopover(true)}>
              <MagnifyIcon />
            </InputGroup.Text>
          </Popover>
          {/* )} */}
        </InputGroup>
      </td>
      <td>
        <Form.Control
          name="Item_Name"
          value={formik.values.Item_Name}
          onChange={formik.handleChange}
          readOnly={!isNewRow}
        />
      </td>
      <td>
        <Form.Control
          type="number"
          name="Quantity"
          value={formik.values.Quantity}
          onChange={formik.handleChange}
          min="1"
          disabled={isNewRow && !formik.values.Bar_Code}
        />
      </td>
      <td>
        <Form.Control
          name="UnitCost"
          value={currency(formik.values.UnitCost, { symbol: "" }).format()}
          readOnly
        />
      </td>
      <td>
        <Form.Control readOnly value={plan?.Product_Model || "N/A"} />
      </td>
      {initProps && <td>{plan.complete ? "Fulfilled" : "Unfulfilled"}</td>}
      <td>
        <DateTime
          timeFormat={false}
          closeOnSelect={true}
          closeOnClickOutside={true}
          dateFormat="MMM DD, YYYY"
          name="dueDate"
          ref={DateTimeRef}
          inputProps={{
            style: { zIndex: "100" },
            className: "date-input form-control",
            placeholder: "Select date",
            readOnly: true,
          }}
          value={
            plan.dueDate
              ? new Date(plan.dueDate)
              : new Date(formik.values.dueDate)
          }
          onChange={(date) => formik.setFieldValue("dueDate", date, true)}
          disabled={isNewRow && !formik.values.Bar_Code}
        />
      </td>
    </tr>
  );
};
